//@flow
import { ProjectMapper } from 'mappers/project';
import type { ProductFilters } from 'types/filter';
import type { Product, VersionType } from 'types/product';
import type { MediaPath } from 'types/common';
import { reduce, map } from 'lodash';
import { NumberUtil } from 'utils/NumberUtil';
import { StringUtil } from 'utils/StringUtil';

const mapProducts = (products: any): Array<Product> => {
  return products.map(product => mapProject(product));
};

const mapProject = (data: any): Array<Product> => {
  return {
    id: data.product_id,
    projectId: data.project_id,
    bedRooms: data.product.number_of_bedrooms,
    propertyType: data.product.property_type_id,
  };
};

const mapVersions = (
  versions: Array<VersionType>,
  mediaPath: MediaPath
): VersionType => {
  if (!versions || versions.length === 0) {
    return [];
  }
  let data;
  data = reduce(
    versions,
    (result, value) => {
      return mapVersion(result, value, mediaPath);
    },
    {}
  );

  const versionList = map(data, value => {
    value.levels = map(value.levels, level => level);
    return value;
  });

  return versionList;
};

const mapVersion = (result: any, value: any, mediaPath: MediaPath) => {
  if (value.VersionAreas.status === 'A') {
    if (!result[value.version_id]) {
      result[value.version_id] = formatVersion(value, mediaPath);
    } else {
      if (!result[value.version_id].levels[value.level_id]) {
        result[value.version_id].levels[value.level_id] = mapLevels(
          value,
          mediaPath
        );
      } else {
        result[value.version_id].levels[value.level_id].configuration.push(
          mapConfigurations(value, mediaPath)
        );
      }
    }
  }
  return result;
};

const formatVersion = (data: any, mediaPath: MediaPath) => {
  const versionArea = data.VersionAreas || [];
  if (!versionArea || versionArea.length === 0) {
    return [];
  }
  const product = getProductData(data.product);
  let levels = {};
  levels[data.level_id] = mapLevels(data, mediaPath);
  return {
    id: data.version_id,
    name: versionArea.version_name,
    carpetArea: NumberUtil.formatNumber(versionArea.carpet_area),
    indoorSpace: NumberUtil.formatNumber(versionArea.indoor_space),
    outdoorSpace: NumberUtil.formatNumber(versionArea.outdoor_space),
    referenceSaleableArea: NumberUtil.formatNumber(
      versionArea.reference_saleable_area
    ),
    saleableArea: NumberUtil.formatNumber(versionArea.saleable_area),
    status: data.status,
    floorPlan: data.floor_plan,
    product,
    levels,
    carpet_area_label: versionArea.carpet_area
      ? versionArea.carpet_area_label
      : '',
    indoor_space_label: versionArea.indoor_space
      ? versionArea.indoor_space_label
      : '',
    outdoor_space_label: versionArea.outdoor_space
      ? versionArea.outdoor_space_label
      : '',
    reference_saleable_area_label: versionArea.reference_saleable_area
      ? versionArea.reference_saleable_area_label
      : '',
    built_up_label: versionArea.saleable_area
      ? versionArea.saleable_area_label
      : '',
  };
};

const mapLevels = (data: any, mediaPath: MediaPath) => {
  const configuration = [mapConfigurations(data, mediaPath)];
  return {
    id: data.level_id,
    name: data.level.level_name,
    configuration,
  };
};

const mapConfigurations = (data: any, mediaPath: MediaPath) => {
  const hotspotArray = data.floor_plan_hotspots.map(hotspot =>
    mapHotspot(hotspot, mediaPath)
  );
  return {
    id: data.configuration_id,
    name: data.configuration.configuration_name,
    view_name: data.view_name ? data.view_name : '',
    listImageUrl: `${mediaPath.web}/${data.picture}`,
    mobileImageUrl: data.mobile_picture
      ? `${mediaPath.mobile}/${data.mobile_picture}`
      : `${mediaPath.web}/${data.picture}`,
    isSelected: false,
    hotSpots: hotspotArray,
  };
};

const mapHotspot = (data: any, mediaPath: MediaPath) => {
  return {
    id: data.id,
    heading: data.heading,
    description: data.description ? StringUtil.sliceHtml(data.description) : '',
    indoorParameter: data.indoor_parameter,
    indoorSpaces: data.indoor_spaces,
    mobileDescription: data.mobile_description
      ? StringUtil.sliceHtml(data.mobile_description)
      : '',
    thumbNailUrl: `${mediaPath.thumbnail}/${data.picture}`,
    mobileImage: `${mediaPath.mobile}/${data.mobile_picture}`,
    outdoorParameter: data.outdoor_parameter,
    outdoorSpaces: data.outdoor_spaces,
    desktopImage: `${mediaPath.web}/${data.picture}`,
    x_coordinate: data.x_coordinate,
    y_coordinate: data.y_coordinate,
  };
};

const getProductData = data => {
  return {
    id: data.id,
    name: data.product_name,
    version: data.product_version,
    bedRooms: data.number_of_bedrooms,
    bathRooms: data.number_of_bathrooms,
    balconies: data.number_of_balconies,
    description: data.short_content,
    urlSlug: data.url_slug,
    productBrochureUrl: `https://carmatec.blob.core.windows.net/te-products/Files/${data.product_brochure}`,
    productBrochure: data.product_brochure,
    brochure_campaign_code: data.brochure_campaign_code,
  };
};

const extractProductFilters = (products: Array<any>): ProductFilters => {
  const area = {
    min: 10000,
    max: 1,
  };

  products.forEach(product => {
    const productDetails = product.detailCard;
    const maxArea = parseFloat(productDetails.max_area.replace(',', ''));
    const minArea = parseFloat(productDetails.min_area.replace(',', ''));
    if (area.min > minArea) {
      area.min = minArea;
    }
    if (area.max < maxArea) {
      area.max = maxArea;
    }
  });

  return {
    area,
  };
};

export const ProductMapper = {
  mapProducts,
  mapVersions,
  extractProductFilters,
};
